// @tennis-matches/ui-components/ballmates-loader
// Tennis-themed page transition loader (Seitenwechsel). Generic for all apps.
// Pass optional t(key) for i18n; otherwise fallback strings are used.

import React, { useEffect, useState } from "react";

const SEITENWECHSEL_KEYS = [
  "ballmates.loader.seitenwechsel.1",
  "ballmates.loader.seitenwechsel.2",
  "ballmates.loader.seitenwechsel.3",
  "ballmates.loader.seitenwechsel.4",
  "ballmates.loader.seitenwechsel.5",
];

const SEITENWECHSEL_FALLBACKS = [
  "Seitenwechsel...",
  "Kurze Trinkpause...",
  "Handtuch holen...",
  "Durchatmen...",
  "Gleich geht's weiter...",
];

function TennisBallSvg({ className, size = 40 }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      width={size}
      height={size}
      aria-hidden
    >
      <circle cx="16" cy="16" r="14" fill="#c8e426" />
      <path
        d="M 6 16 Q 16 8, 26 16"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M 6 16 Q 16 24, 26 16"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Main loader: tennis ball rally + rotating messages.
 * @param {{ message?: string, className?: string, t?: (key: string) => string }} props
 */
export function BallmatesLoader({ message, className = "", t }) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (message) return;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % SEITENWECHSEL_KEYS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [message]);

  const displayMessage =
    message ||
    (typeof t === "function"
      ? t(SEITENWECHSEL_KEYS[messageIndex]) || SEITENWECHSEL_FALLBACKS[messageIndex]
      : SEITENWECHSEL_FALLBACKS[messageIndex]);

  return (
    <div className={`ballmates-loader ${className}`.trim()} role="status" aria-live="polite" aria-label={displayMessage}>
      <div className="ballmates-loader__balls">
        <div className="ballmates-loader__ball-wrap ballmates-loader__ball-wrap--1 ballmates-loader__rally-1">
          <TennisBallSvg className="ballmates-loader__ball" size={40} />
        </div>
        <div className="ballmates-loader__ball-wrap ballmates-loader__ball-wrap--2 ballmates-loader__rally-2">
          <TennisBallSvg className="ballmates-loader__ball" size={40} />
        </div>
      </div>
      <div className="ballmates-loader__dots" aria-hidden>
        <span className="ballmates-loader__dot" />
        <span className="ballmates-loader__dot ballmates-loader__dot--middle" />
        <span className="ballmates-loader__dot" />
      </div>
      <p className="ballmates-loader__message">{displayMessage}</p>
    </div>
  );
}

/**
 * Mini spinner for buttons (e.g. "Speichern...").
 * @param {{ className?: string, size?: number, t?: (key: string) => string }} props
 */
export function BallmatesSpinner({ className = "", size = 20, t }) {
  const ariaLabel = typeof t === "function" ? t("common.loading.aria") || "Lädt" : "Lädt";
  return (
    <span className={`ballmates-spinner ${className}`.trim()} role="img" aria-label={ariaLabel}>
      <TennisBallSvg className="ballmates-spinner__svg" size={size} />
    </span>
  );
}

/**
 * Fullscreen overlay with main loader. Use with Suspense or route transitions.
 * @param {{ message?: string, t?: (key: string) => string }} props
 */
export function BallmatesFullPageLoader({ message, t }) {
  return (
    <div className="ballmates-fullpage" role="alert" aria-busy="true">
      <BallmatesLoader message={message} t={t} />
    </div>
  );
}
